import { reaction } from 'mobx';
import { MobXProviderContext } from 'mobx-react';
import { SoftphoneAudios } from 'Modules/softphone/components';
import { useSoftphoneKeyboardShortcuts } from 'Modules/softphone/hooks/useSoftphoneKeyboardShortcuts';
import { useSoftphoneVersionSync } from 'Modules/softphone/hooks/useSoftphoneVersionSync';
import { useFetchSipConfig } from 'Modules/softphone/requests/index.requests';
import { ConnectionManager } from 'Modules/softphone/services/ConnectionManager';
import {
  useSoftphoneCoordinatorStore,
  useSoftphoneStore,
} from 'Modules/softphone/store';
import { providerLogger as logger } from 'Modules/softphone/utils/logger';
import { SoftphoneCoordinator } from 'Modules/softphone/utils/softphoneCoordinator';
import React, { useEffect } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { SoftphoneDevTools } from 'Modules/softphone/components/SoftphoneDevTools'

interface SoftphoneProviderProps {
  children: React.ReactNode;
}

export const SoftphoneProvider: React.FC<SoftphoneProviderProps> = ({
  children,
}) => {
  const rootStoreProps = React.useContext<RootStoreProps>(MobXProviderContext);
  const { person } = rootStoreProps;

  const connectionManager = ConnectionManager.getInstance();
  const coordinator = SoftphoneCoordinator.getInstance();
  const store = useSoftphoneStore();

  const personId = person?.loggedInPersonId;
  const softphoneCoordinatorStore = useSoftphoneCoordinatorStore(personId);
  const isNewSoftphoneEnabled = softphoneCoordinatorStore(
    (state) => state.isNewSoftphoneEnabled
  );

  useSoftphoneKeyboardShortcuts(isNewSoftphoneEnabled);

  /**
   * The `communicator-softphone-v2` feature flag is the only control over which
   * softphone runs. Keep the persisted version aligned with it, reloading when
   * it changes so the session starts with a clean SIP registration (PhoneStore
   * only registers legacy SIP once, on login).
   */
  useSoftphoneVersionSync();

  // Track if user is logged in
  const [shouldFetch, setShouldFetch] = React.useState(false);

  // Wait for user to be logged in before fetching SIP config
  React.useEffect(() => {
    if (!person) {
      logger.warn('PersonStore not available in context');
      return;
    }

    logger.debug('Subscribing to login state changes...');

    const dispose = reaction(
      () => person.IsLoggedIn,
      (isLoggedIn) => {
        if (isLoggedIn) {
          logger.info('User logged in, enabling SIP config fetch', {
            personId: person.loggedInPersonId,
          });
          setShouldFetch(true);
        } else {
          // User logged out — tear down SIP so next login re-initializes
          logger.info('User logged out, cleaning up SIP connection');
          setShouldFetch(false);
          void connectionManager.cleanup();
        }
      },
      { fireImmediately: true }
    );

    return () => dispose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person]);

  logger.debug('SoftphoneProvider state', {
    personId,
    shouldFetch,
    isNewSoftphoneEnabled,
  });

  const { data: sipConfig, isSuccess, error } = useFetchSipConfig(shouldFetch);

  /**
   * Configure ConnectionManager once with dependencies
   * Runs only once on mount
   */
  useEffect(() => {
    if (!connectionManager.getIsConfigured()) {
      // Pass rootStoreProps directly - it contains the individual stores
      connectionManager.configure(store, coordinator, rootStoreProps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handles the entire softphone initialization sequence:
   * 1. Initialize SIP connection
   * 2. Initialize audio devices
   *
   * SIP failures stop the sequence; device failures log a warning but continue.
   *
   * IMPORTANT: We prevent cleanup during active calls to avoid re-registration.
   * If isNewSoftphoneEnabled changes to false while calls are active, we defer cleanup
   * until all calls are ended.
   */
  useEffect(() => {
    logger.debug('SoftphoneProvider useEffect triggered', {
      isNewSoftphoneEnabled,
      isSuccess,
      hasError: !!error,
      hasSipConfig: !!sipConfig,
      isInitialized: connectionManager.getIsInitialized(),
      personId,
      shouldFetch,
    });

    // Skip if new softphone is disabled
    if (!isNewSoftphoneEnabled) {
      logger.debug('New softphone disabled, skipping initialization');
      return;
    }

    // Skip if no config yet
    if (!isSuccess || !sipConfig) {
      logger.warn('Waiting for SIP config', {
        isSuccess,
        hasSipConfig: !!sipConfig,
        shouldFetch,
      });
      return;
    }

    // Handle config fetch errors
    if (error) {
      logger.error('Config fetch error', { error });
      connectionManager.handleConfigError(error);
      return;
    }

    // Skip if already initialized
    if (connectionManager.getIsInitialized()) {
      logger.debug('ConnectionManager already initialized, skipping');
      return;
    }

    const initialize = async () => {
      try {
        // Step 1: Initialize SIP
        logger.info('Step 1: SIP config received, initializing...', {
          config: {
            uri: sipConfig.uri,
            domain: sipConfig.domain,
            wsServers: sipConfig.wsServers,
          },
        });
        const sipSuccess = await connectionManager.initializeSip(sipConfig);

        // If SIP fails, stop sequence
        if (!sipSuccess) {
          logger.error('SIP initialization failed, stopping sequence');
          return;
        }

        // Step 2: Initialize devices (non-blocking - log warning on failure)
        logger.debug('Step 2: Initializing devices...');
        const devicesSuccess = await connectionManager.initializeDevices();
        if (!devicesSuccess) {
          // Log warning but continue
          logger.warn(
            'Device initialization failed, continuing without devices'
          );
        }

        logger.info('Initialization sequence completed successfully');
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Initialization sequence failed', error);
      }
    };

    void initialize();

    // Cleanup on unmount or when disabled
    return () => {
      // Check current call state at cleanup time
      const currentActiveCalls = useSoftphoneStore.getState().activeCalls.size;

      // If there are active calls, defer cleanup to prevent re-registration
      if (currentActiveCalls > 0) {
        logger.warn(
          'Provider cleanup requested but active calls exist - deferring cleanup to prevent re-registration'
        );
        return;
      }

      logger.debug('Provider unmounting, cleaning up...');
      void connectionManager.cleanup();
    };
  }, [isNewSoftphoneEnabled, isSuccess, error, sipConfig]);

  return (
    <SoftphoneDevTools>
      {/*
        Render audio elements in DOM to bypass autoplay restrictions.
        IMPORTANT: Keep mounted even when softphone is disabled if there are active calls
        to prevent audio interruption.
      */}
      {isNewSoftphoneEnabled && <SoftphoneAudios />}
      {children}
    </SoftphoneDevTools>
  );
};
